import { Button } from 'components/universal/button';
import { Controller } from 'react-hook-form';
import { AsyncPaginate } from 'react-select-async-paginate';
import { employeesInterface } from 'types/employee';
import { DropdownIndicator } from '../addMultipleUser';
import { Input } from '../input';
import cls from './editUserData.module.scss';
import { useEditUserDataForm } from './useEditUserDataForm';

export interface EditUserDataValue {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    userIds: string;
    employee_position_id: number | null;
    department_id: string | number;
};

export type EditUserRequest = {
    data: EditUserDataValue
}

interface EditUserDataFormFormInterface {
    user: employeesInterface[];
    workplaceId: number;
    onCancel: (boolean) => void;
}

export const EditUserDataForm = ({
    user,
    workplaceId,
    onCancel
}: EditUserDataFormFormInterface) => {
    const {
        t,
        register,
        handleSubmit,
        control,
        errors,
        onSubmit,
        selectedPosition,
        setSelectedPosition,
        loadPositionOptions,
        selectedDepartment,
        setSelectedDepartment,
        loadDepartmentOptions,
    } = useEditUserDataForm({ user, workplaceId, onCancel });

    return (
        <>
            <h3 className={cls.modalHeader}>{t('employeeManagement.editUserData')}</h3>
            <form
                className={cls.form}
                onSubmit={handleSubmit(onSubmit)}
                noValidate
            >
                <input {...register('userIds')} type="hidden" />
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="first_name"
                    name="first_name"
                    label={t('registration.firstNameRequire')}
                    error={t(errors.first_name?.message)}
                    maxLength={255}
                />
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="last_name"
                    name="last_name"
                    label={t('registration.lastNameRequire')}
                    error={t(errors.last_name?.message)}
                    maxLength={255}
                />
                <div className={`${cls.input}`}>
                    <label className={cls.label}>
                        {t('registration.positionRequire')}
                    </label>
                    <Controller
                        name="employee_position_id"
                        control={control}
                        render={({ field }) => (
                            <AsyncPaginate
                                className={errors.employee_position_id ? 'error react-select-container' : 'react-select-container'}
                                classNamePrefix="react-select"
                                components={{ DropdownIndicator }}
                                placeholder=""
                                value={selectedPosition}
                                loadOptions={loadPositionOptions}
                                additional={{ page: 1 }}
                                defaultOptions
                                onChange={(selected: { label: string; value: number } | null) => {
                                    setSelectedPosition(selected);
                                    field.onChange(selected?.value ?? null);
                                }}
                                noOptionsMessage={() => t('registration.noPositions')}
                                debounceTimeout={300}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        )}
                    />
                    {errors.employee_position_id && (
                        <p className={cls.errorBox}>{t(errors.employee_position_id.message)}</p>
                    )}
                </div>
                <div className={`${cls.selectCustomWrapper} ${cls.input}`}>
                    <label htmlFor="department_id" className={cls.label}>
                        {t('registration.workplaceDepartments')}
                    </label>
                    <Controller
                        name="department_id"
                        control={control}
                        render={({ field }) => (
                            <AsyncPaginate
                                key={workplaceId}
                                className={errors.department_id ? 'error react-select-container' : 'react-select-container'}
                                classNamePrefix="react-select"
                                components={{ DropdownIndicator }}
                                placeholder=""
                                value={selectedDepartment}
                                loadOptions={loadDepartmentOptions}
                                additional={{ page: 1 }}
                                onChange={(selected: { label: string; value: number } | null) => {
                                    setSelectedDepartment(selected);
                                    field.onChange(selected?.value ?? '');
                                }}
                                noOptionsMessage={() => t('registration.noDepartments')}
                                debounceTimeout={300}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            />
                        )}
                    />
                    {errors.department_id && (
                        <p className={cls.errorBox}>{t(errors.department_id.message).replace('department id', t('registration.firstLoginModal.department').toLowerCase())}</p>
                    )}
                </div>
                <div className={cls.modalButtonWrapper}>
                    <Button className={cls.modalButton} styleType="border" type="button" onClick={onCancel}>
                        {t('employeeManagement.cancel')}
                    </Button>
                    <Button className={cls.modalButton} type="submit">
                        {t('employeeManagement.send2')}
                    </Button>
                </div>
            </form>
        </>
    );
};
